import { Button, Checkbox, Col, Row, Slider, Typography } from 'antd';
import { Music, Scene, SceneAudioItem } from '@monorepo/types';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { SoundOutlined } from '@ant-design/icons';

import AudioModal, {
  AudioModalActions,
} from '../../../../../audio-modal/audio-modal';
import { useVideoContext } from '../../../../../../hooks/use-video';
import { useStore } from '../../../../../../helpers/use-store';
import { useTranslation } from 'react-i18next';

export type EditAudioForm = Pick<
  SceneAudioItem,
  'volume' | 'loop' | 'playbackRate'
>;

const EditAudio = () => {
  const { t } = useTranslation();
  const audioModalRef = useRef<AudioModalActions>(null);
  const form = useForm<EditAudioForm>();

  const {
    updateSceneItem,
    selectedScene: scene,
    selectedSceneItemIndex,
    setSceneId,
  } = useVideoContext();

  const {
    dataStore: { musicStore },
  } = useStore();

  const item = scene?.items[selectedSceneItemIndex];
  const audioData = item?.data as SceneAudioItem;

  const resetForm = () => {
    form.reset({
      volume: audioData.volume || 1,
      loop: audioData.loop || false,
      playbackRate: audioData.playbackRate || 1,
    });
  };

  useEffect(() => {
    resetForm();
  }, [scene?._id]);

  // Helper function to update server
  const updateServer = async () => {
    if (!scene) return;

    await updateSceneItem({
      sceneId: scene._id,
      videoId: scene.videoId,
      updateDto: {
        data: form.getValues(),
      },
      itemIndex: selectedSceneItemIndex,
    });
  };

  // Handle volume change during slider movement (update form only)
  const handleVolumeChange = (value: number) => {
    form.setValue('volume', value);
  };

  // Handle volume change when slider interaction is complete (update form and server)
  const handleVolumeChangeComplete = (value: number) => {
    form.setValue('volume', value);
    updateServer();
  };

  // Handle playback rate change during slider movement (update form only)
  const handlePlaybackRateChange = (value: number) => {
    form.setValue('playbackRate', value);
  };

  // Handle playback rate change when slider interaction is complete (update form and server)
  const handlePlaybackRateChangeComplete = (value: number) => {
    form.setValue('playbackRate', value);
    updateServer();
  };

  // Handle loop toggle (update form and server)
  const handleLoopChange = (checked: boolean) => {
    form.setValue('loop', checked);
    updateServer();
  };

  const handleOpenAudioModal = () => {
    if (!scene) {
      return;
    }

    audioModalRef.current?.open();
    setSceneId(scene?._id);
  };

  if (!scene) {
    return null;
  }

  return (
    <Row className={'edit-audio'} gutter={[10, 15]}>
      <AudioModal
        itemIndex={selectedSceneItemIndex}
        ref={audioModalRef}
        sceneId={scene._id}
        videoId={scene.videoId}
      />

      <Col span={24}>
        <Row align="middle" gutter={[10, 10]}>
          <Col span={6}>
            <Typography.Text>
              {t('videoEditor.sceneAudio.selectedAudio')}:
            </Typography.Text>
          </Col>
          <Col span={18}>
            {audioData.url ? (
              <Typography.Text strong>{audioData.name}</Typography.Text>
            ) : (
              <Typography.Text type="secondary">
                {t('videoEditor.sceneAudio.noAudioSelected')}
              </Typography.Text>
            )}
            <Button
              icon={<SoundOutlined />}
              type="primary"
              size="small"
              style={{ marginLeft: '10px' }}
              onClick={handleOpenAudioModal}
            >
              {t('videoEditor.sceneAudio.changeAudio')}
            </Button>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Row align="middle" gutter={[10, 10]}>
          <Col span={6}>
            <Typography.Text>
              {t('videoEditor.sceneAudio.volume')}:
            </Typography.Text>
          </Col>
          <Col span={18}>
            <Slider
              min={0}
              max={2}
              step={0.1}
              tooltip={{ formatter: (value) => `${value}x` }}
              value={form.watch('volume') || 1}
              onChange={handleVolumeChange}
              onChangeComplete={handleVolumeChangeComplete}
              style={{ marginBottom: 0 }}
            />
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Row align="middle" gutter={[10, 10]}>
          <Col span={6}>
            <Typography.Text>
              {t('videoEditor.sceneAudio.playbackRate')}:
            </Typography.Text>
          </Col>
          <Col span={18}>
            <Slider
              min={0}
              max={2}
              step={0.1}
              value={form.watch('playbackRate') || 1}
              onChange={handlePlaybackRateChange}
              onChangeComplete={handlePlaybackRateChangeComplete}
              tooltip={{ formatter: (value) => `${value}x` }}
              style={{ marginBottom: 0 }}
            />
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Row align="middle" gutter={[10, 10]}>
          <Col span={6}>
            <Typography.Text>
              {t('videoEditor.sceneAudio.loop')}:
            </Typography.Text>
          </Col>
          <Col span={18}>
            <Checkbox
              checked={form.watch('loop') || false}
              onChange={(e) => handleLoopChange(e.target.checked)}
            >
              {t('videoEditor.sceneAudio.loop')}
            </Checkbox>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default EditAudio;
